import { useEffect } from 'react';
import { Package, Layers, Repeat, Star } from 'lucide-react';
import GlobalSearch from './GlobalSearch';
import { useCollection } from '../hooks/useCollection';
import { useSocial } from '../hooks/useSocial';
import { computeTradeables } from '../utils/tradeables';
import { getSeriesColor, getSeriesCompletion, sortSeriesByLatest } from '../utils/seriesColors';
import { formatCurrency } from '../utils/portfolioValue';

const StatCard = ({ label, value, valueClassName }) => (
  <div className="bg-white border border-border rounded-2xl p-5">
    <div className="text-[12px] uppercase tracking-wide text-ink-muted mb-1">{label}</div>
    <div className={`font-heading font-extrabold text-2xl md:text-3xl ${valueClassName || 'text-ink-primary'}`}>
      {value}
    </div>
  </div>
);

const ComingSoonPanel = ({ title, description }) => (
  <div className="bg-white border border-border rounded-2xl p-5">
    <h3 className="font-heading font-bold text-sm text-ink-primary mb-1">{title}</h3>
    <p className="text-[12px] text-ink-secondary">{description}</p>
  </div>
);

const Dashboard = ({ onNavigate, onSearchNavigate }) => {
  const {
    getCollectionStats,
    packStats,
    getGlobalHitRate,
    getPortfolioValue,
    portfolioLoading,
    getSeriesGroups,
    allSets,
    collection,
    serverOnline,
    loadAllSets,
    checkServerHealth,
    settings
  } = useCollection();

  const { wishlistItems, friends, incoming, tradeOverrides, matches } = useSocial();

  const activeMatches = matches.filter((m) => m.score > 0);
  const totalMatchedCards = activeMatches.reduce((sum, m) => sum + m.score, 0);

  const tradeThreshold = Number.isInteger(settings.tradeThreshold) ? settings.tradeThreshold : 1;
  const tradeables = computeTradeables(collection, tradeOverrides, tradeThreshold).filter(
    (r) => r.tradeableQty > 0
  );
  const tradeableCopies = tradeables.reduce((sum, r) => sum + r.tradeableQty, 0);

  // Dashboard is now the landing view, so it — not just SeriesGrid — needs
  // to bootstrap the server-health check and set list. Mirrors SeriesGrid's
  // own bootstrap effect; harmless to run in parallel since only one of the
  // two is ever mounted at a time.
  useEffect(() => {
    checkServerHealth();
  }, [checkServerHealth]);

  useEffect(() => {
    if (serverOnline && allSets.length === 0) {
      loadAllSets().catch(console.error);
    }
  }, [serverOnline, allSets.length, loadAllSets]);

  const stats = getCollectionStats();
  const hitRate = getGlobalHitRate();
  const portfolioValue = getPortfolioValue();
  const seriesGroups = getSeriesGroups();
  const seriesList = sortSeriesByLatest(seriesGroups);

  const seriesProgress = seriesList.slice(0, 5).map((series) => {
    const completion = getSeriesCompletion(seriesGroups[series], collection);
    return { series, percentage: completion.percentage, color: getSeriesColor(series) };
  });

  // Best-effort recent activity from persisted pack-opening dates. There is
  // no card-level activity log in the data model — this is a deliberate
  // approximation, not a full activity feed. A real feed would need a new
  // server-side event log, which is out of scope here.
  const recentActivity = Object.entries(packStats.setStats || {})
    .filter(([, data]) => data.lastOpened)
    .sort((a, b) => new Date(b[1].lastOpened) - new Date(a[1].lastOpened))
    .slice(0, 5)
    .map(([setId, data]) => {
      const set = allSets.find((s) => s.id === setId);
      return { setId, setName: set?.name || setId, date: data.lastOpened };
    });

  return (
    <div className="px-4 md:px-10 py-6">
      {/* Global card search — the only search reachable without drilling
          into the Collection tab, so it lives at the very top */}
      <div className="mb-6 md:max-w-xl">
        <GlobalSearch onNavigate={onSearchNavigate} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatCard label="Cards" value={stats.totalCards.toLocaleString()} valueClassName="text-yellow-dark" />
        <StatCard label="Unique" value={stats.uniqueCards.toLocaleString()} />
        <StatCard label="Packs" value={packStats.totalPacks.toLocaleString()} />
        <StatCard label="Hit Rate" value={hitRate === null ? '—' : `${hitRate}%`} valueClassName="text-success-green" />
        <StatCard
          label="Portfolio Value"
          value={portfolioLoading ? '…' : formatCurrency(portfolioValue)}
          valueClassName="text-amber"
        />
      </div>

      {/* Mobile quick actions */}
      <div className="md:hidden grid grid-cols-3 gap-2 mb-6">
        <button
          onClick={() => onNavigate('series')}
          className="flex flex-col items-center gap-1.5 bg-white border border-border rounded-xl py-3"
        >
          <Layers size={18} className="text-navy-dark" />
          <span className="text-[11px] font-medium text-ink-primary">Browse Sets</span>
        </button>
        <button
          onClick={() => onNavigate('series')}
          className="flex flex-col items-center gap-1.5 bg-white border border-border rounded-xl py-3"
        >
          <Package size={18} className="text-navy-dark" />
          <span className="text-[11px] font-medium text-ink-primary">Log Packs</span>
        </button>
        <button
          onClick={() => onNavigate('trades')}
          className="flex flex-col items-center gap-1.5 bg-white border border-border rounded-xl py-3"
        >
          <Repeat size={18} className="text-navy-dark" />
          <span className="text-[11px] font-medium text-ink-primary">Trades</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="space-y-5">
          <div className="bg-white border border-border rounded-2xl p-5">
            <h3 className="font-heading font-bold text-sm text-ink-primary mb-4">Collection Progress</h3>
            {seriesProgress.length === 0 ? (
              <p className="text-[12px] text-ink-secondary">Load some sets to see progress.</p>
            ) : (
              <div className="space-y-3">
                {seriesProgress.map((s) => (
                  <div key={s.series}>
                    <div className="flex items-center justify-between text-[12px] mb-1">
                      <span className="text-ink-primary font-medium">{s.series}</span>
                      <span className="text-ink-secondary">{s.percentage}%</span>
                    </div>
                    <div className="h-[5px] bg-cream-bg rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-[width] duration-400 ease-out"
                        style={{ width: `${s.percentage}%`, backgroundColor: s.color }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-border rounded-2xl p-5">
            <h3 className="font-heading font-bold text-sm text-ink-primary mb-4">Recent Activity</h3>
            {recentActivity.length === 0 ? (
              <p className="text-[12px] text-ink-secondary">No packs opened yet.</p>
            ) : (
              <div className="space-y-3">
                {recentActivity.map((a) => (
                  <div key={a.setId} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-yellow/10 border border-yellow/20 flex items-center justify-center flex-shrink-0">
                      <Package size={14} className="text-yellow-dark" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[12px] font-medium text-ink-primary truncate">
                        Opened packs — {a.setName}
                      </div>
                    </div>
                    <div className="text-[11px] text-ink-muted flex-shrink-0">{a.date}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-5">
          <div
            onClick={() => onNavigate('wishlist')}
            className="bg-white border border-border rounded-2xl p-5 cursor-pointer hover:shadow-card transition-shadow"
          >
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading font-bold text-sm text-ink-primary">Wishlist</h3>
              <span className="text-[12px] font-semibold text-yellow-dark">
                {wishlistItems.length} item{wishlistItems.length === 1 ? '' : 's'}
              </span>
            </div>
            {wishlistItems.length === 0 ? (
              <p className="text-[12px] text-ink-secondary">
                Star the versions you're hunting for and they'll show up here.
              </p>
            ) : (
              <div className="flex items-center gap-2 mt-3">
                {wishlistItems.slice(0, 3).map((item) => (
                  <img
                    key={item.cardId}
                    src={item.cardImage}
                    alt={item.cardName}
                    title={item.cardName}
                    className="w-12 h-16 object-contain rounded border border-border bg-cream-bg"
                  />
                ))}
                {wishlistItems.length > 3 && (
                  <span className="text-[12px] text-ink-muted ml-1">
                    +{wishlistItems.length - 3} more
                  </span>
                )}
              </div>
            )}
          </div>
          <div
            onClick={() => onNavigate('trades')}
            className="bg-white border border-border rounded-2xl p-5 cursor-pointer hover:shadow-card transition-shadow"
          >
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading font-bold text-sm text-ink-primary">Trade Matches</h3>
              {totalMatchedCards > 0 && (
                <span className="text-[12px] font-semibold text-success-green">
                  {totalMatchedCards} match{totalMatchedCards === 1 ? '' : 'es'}
                </span>
              )}
            </div>
            {activeMatches.length === 0 ? (
              <p className="text-[12px] text-ink-secondary">
                When a friend has a card you want (or wants one of your spares), it shows up here.
              </p>
            ) : (
              <p className="text-[12px] text-ink-secondary">
                {activeMatches[0].friend.username} has {activeMatches[0].theyHave.length} card
                {activeMatches[0].theyHave.length === 1 ? '' : 's'} you want
                {activeMatches.length > 1 ? ` — and ${activeMatches.length - 1} more friend${activeMatches.length > 2 ? 's' : ''} matched` : ''}.
              </p>
            )}
          </div>
        </div>

        <div className="space-y-5">
          <div
            onClick={() => onNavigate('friends')}
            className="bg-white border border-border rounded-2xl p-5 cursor-pointer hover:shadow-card transition-shadow"
          >
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading font-bold text-sm text-ink-primary">Friends</h3>
              <span className="text-[12px] font-semibold text-ink-secondary">{friends.length}</span>
            </div>
            {incoming.length > 0 ? (
              <p className="text-[12px] text-brick font-medium">
                {incoming.length} pending friend request{incoming.length === 1 ? '' : 's'} — tap to review
              </p>
            ) : friends.length === 0 ? (
              <p className="text-[12px] text-ink-secondary">
                Add friends by username to start finding trades.
              </p>
            ) : (
              <p className="text-[12px] text-ink-secondary">
                {friends.map((f) => f.username).slice(0, 3).join(', ')}
                {friends.length > 3 ? ` +${friends.length - 3} more` : ''}
              </p>
            )}
          </div>
          <div
            onClick={() => onNavigate('trades')}
            className="bg-white border border-border rounded-2xl p-5 cursor-pointer hover:shadow-card transition-shadow"
          >
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading font-bold text-sm text-ink-primary">Duplicates for Trade</h3>
              <span className="text-[12px] font-semibold text-success-green">
                {tradeableCopies} {tradeableCopies === 1 ? 'copy' : 'copies'}
              </span>
            </div>
            {tradeables.length === 0 ? (
              <p className="text-[12px] text-ink-secondary">
                Cards with more than {tradeThreshold} {tradeThreshold === 1 ? 'copy' : 'copies'} of a
                version get offered automatically.
              </p>
            ) : (
              <p className="text-[12px] text-ink-secondary">
                {tradeables.length} card version{tradeables.length === 1 ? '' : 's'} ready to trade —
                tap to manage.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
